/**
 * Program API
 *
 * A program is the practice area. Hubs are assigned through programId.
 */

import {
  PlaceKind,
  Program,
  ProgramHighlight,
  ProgramSection,
  ProgramStatus,
} from '../types/program';
import { clerkAuthHeaders } from './clerkApiAuth';

const API_BASE_URL = '/api';

async function getApiErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.error === 'string' && body.error.trim()) {
      return `${fallback}: ${body.error}`;
    }
  } catch {
    // Response body wasn't JSON
  }
  return `${fallback}: ${response.statusText || response.status}`;
}

class ProgramService {
  async fetchPrograms(options?: { includeDrafts?: boolean }): Promise<Program[]> {
    const params = options?.includeDrafts ? '?includeDrafts=true' : '';
    const response = await fetch(`${API_BASE_URL}/programs${params}`, {
      headers: options?.includeDrafts ? await clerkAuthHeaders({ json: false }) : undefined,
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to fetch programs'));
    return response.json();
  }

  async fetchProgramBySlug(slug: string): Promise<Program | null> {
    const response = await fetch(`${API_BASE_URL}/programs/slug/${encodeURIComponent(slug)}`);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to fetch program'));
    return response.json();
  }

  async fetchProgram(id: string): Promise<Program> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}?includeDrafts=true`, {
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to fetch program'));
    return response.json();
  }

  async createProgram(input: {
    title: string;
    slug: string;
    introduction?: string;
    kind?: PlaceKind;
    interestTopics?: string[];
    status?: ProgramStatus;
    sortOrder?: number;
  }): Promise<Program> {
    const response = await fetch(`${API_BASE_URL}/programs`, {
      method: 'POST',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to create program'));
    return response.json();
  }

  async updateProgram(
    id: string,
    updates: {
      title?: string;
      slug?: string;
      introduction?: string | null;
      kind?: PlaceKind;
      interestTopics?: string[];
      status?: ProgramStatus;
      sortOrder?: number;
    }
  ): Promise<Program> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}`, {
      method: 'PUT',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to update program'));
    return response.json();
  }

  async deleteProgram(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}`, {
      method: 'DELETE',
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to delete program'));
  }

  async assignHubs(id: string, hubIds: string[]): Promise<Program> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}/hubs`, {
      method: 'PUT',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify({ hubIds }),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to assign hubs'));
    return response.json();
  }

  async createSection(id: string, input: { title: string; body?: string }): Promise<ProgramSection> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}/sections`, {
      method: 'POST',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to create section'));
    return response.json();
  }

  async updateSection(
    id: string,
    sectionId: string,
    updates: { title?: string; body?: string | null; order?: number }
  ): Promise<ProgramSection> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}/sections/${sectionId}`, {
      method: 'PUT',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to update section'));
    return response.json();
  }

  async reorderSections(id: string, sectionIds: string[]): Promise<ProgramSection[]> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}/sections/reorder`, {
      method: 'PUT',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify({ sectionIds }),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to reorder sections'));
    return response.json();
  }

  async deleteSection(id: string, sectionId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}/sections/${sectionId}`, {
      method: 'DELETE',
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to delete section'));
  }

  async addHighlight(id: string, wpPostId: number): Promise<ProgramHighlight> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}/highlights`, {
      method: 'POST',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify({ wpPostId }),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to add highlighted resource'));
    return response.json();
  }

  async reorderHighlights(id: string, highlightIds: string[]): Promise<ProgramHighlight[]> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}/highlights/reorder`, {
      method: 'PUT',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify({ highlightIds }),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to reorder highlighted resources'));
    return response.json();
  }

  async deleteHighlight(id: string, highlightId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/programs/${id}/highlights/${highlightId}`, {
      method: 'DELETE',
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) throw new Error(await getApiErrorMessage(response, 'Failed to remove highlighted resource'));
  }
}

export const programAPI = new ProgramService();
